import React, { useEffect, useRef, useState, useCallback } from "react";
import { AssetSidebarTabs } from "./AssetSidebarTabs";

import Overview from "./Overview";
import LicenseInfo from "./LicenseInfo";
import RaidInfo from "./RaidInfo";
import HardwareInfo from "./HardwareInfo";
import MemoryInfo from "./MemoryInfo";
import PerformanceMetrics from "./PerformanceMetrics";
import SoftwareInfo from "./SoftwareInfo";
import UserList from "./UserList";
import ServiceList from "./ServiceList";
import PatchList from "./PatchList";
import SSL from "./SSL";
import NetworkInterfaces from "./NetworkInterfaces";
import SystemIssues from "./SystemIssues";
import NetworkFirewall from "./NetworkFirewall";
import PCIDevices from "./PCIDevices";
import ProcessList from "./ProcessList";
// import Vulnerabilities from "./Vulnerabilities";
import OpenPorts from "./OpenPorts";
import NewAssetComplianceDashboard from "./compliance/NewAssetComplianceDashboard";
import DiscoveredInterfaces from "./DiscoveredInterfaces";
import DiscoveredNeighbors from "./DiscoveredNeighbors";
import SwitchHardwareInfo from "./SwitchHardwareInfo";
import VlansInfo from "./VlansInfo";
import ArpEntries from "./ArpEntries";
import MacTableInfo from "./MacTableInfo";
import TcpConnections from "./TcpConnections";
import UdpListeners from "./UdpListeners";
import PoeInfo from "./PoeInfo";
import SwitchPorts from "./SwitchPorts";
import SwitchSSH from "./SwitchSSH";
import CameraInterface from "./CameraInterface";
import CameraProtocols from "./CameraProtocols";
import CameraOverview from "./CameraOverview";
import CameraUsers from "./CameraUsers";
import CompactCameraDashboard from "./CompactCameraDashboard";
import ServerProtocols from "./ServerProtocols";
// Import your SNMP components here. Assuming names for example:
// import SnmpInfo from "./SnmpInfo";
// import SnmpHardwareInfo from "./SnmpHardwareInfo";
// import SnmpInterfaces from "./SnmpInterfaces";
import { useNavigate, useLocation } from "react-router-dom";
import SnmpInfo from "./probe/SnmpInfo";
import SnmpHardware from "./probe/SnmpHardware";
import ProbePorts from "./probe/ProbePorts";
import SnmpInterface from "./probe/SnmpInterface";
import baseapi from "../../../services/baseapi";
import { showError, showSuccess } from "../../../utils/toast";
import CompactSwitchDashboard from "./CompactSwitchDashboard";
import { ArrowLeft, Fingerprint, Search, ChevronDown, Loader2, Check, X, ScanSearch } from "lucide-react";
import FirewallMain from "./FirewallDeatils/FirewallMain";
import { useClientLevel } from "../../../hooks/useClientLevel";
import AssetVulnerabilities from "./AssetVulnerabilities";
import AssetServices from "./AssetServices";
import AssetIloIssues from "./AssetIloIssues";
import AssetIloTemperatures from "./AssetIloTemperatures";
import AssetIloEventLogs from "./AssetIloEventLogs";
import AssetIlo from "./AssetIlo";
import AssetIloFirmware from "./AssetIloFirmware";
import AssetIloProtocols from "./AssetIloProtocols";
import AssetWindowsDefender from "./AssetWindowsDefender";
import AssetSharedFolders from "./AssetSharedFolders";
import AssetScheduledTasks from "./AssetScheduledTasks";
import AssetFirewallRules from "./AssetFirewallRules";
import AssetLogonHistory from "./AssetLogonHistory";
import CronJobs from "./CronJobs";
import KernelModules from "./KernelModules";
import AssetContainers from "./AssetContainers";
import AssetSecurity from "./AssetSecurity";
import NvrPage from "./nvr/NvrPage";
import APmain from "./accesspoint/APmain";
import LogMonitor from "./LogsView/LogMonitor";
import AssetDBMonitor from "./AssetDBMonitor";

// Tab names allowed
const ALLOWED_TABS = [
    "overview",
    "license",
    "raid",
    "hardware",
    "memory",
    "performance",
    "software",
    "users",
    "services",
    "patches",
    "security",
    "certificates",
    "interfaces",
    "issues",
    "network-firewall",
    "pci-devices",
    "processes",
    "vulnerabilities",
    "ports",
    "compliance",
    // SNMP specific tabs
    "snmp-info",
    "snmp-hardware",
    "snmp-interfaces",
    'ports_probe',
    "discovered-interfaces",
    "discovered-neighbors",
    "switch-hardware",
    "vlans",
    "arp-entries",
    "mac-table",
    "tcp-connections",
    "udp-listeners",
    "poe",
    "switch-ports",
    "camera-interface",
    "protocol",
    "camera-overview",
    "camera-users",
    "protocol-discovery",
    "service_monitoring",
    "assetsilo",
    "ilo-event-logs",
    "ilo-issues",
    "ilo-temperatures",
    "ilo-firmware",
    "ilo-protocols",
    "windows-defender",
    "shared-folders",
    "scheduled-tasks",
    "firewall-rules",
    "logon-history",
    "cron-jobs",
    "kernel-modules",
    "containers",
    "log-monitor",
    "db-monitor",
];

// Modified to accept a defaultTab
function getTabFromParams(search: string, defaultTab: string): string {
    const params = new URLSearchParams(search);
    const tab = params.get("tab");
    return tab && ALLOWED_TABS.includes(tab) ? tab : defaultTab;
}

// Helper to resolve OS Icon path
const getOSIconPath = (osName?: string) => {
    if (!osName) return "/device.svg";
    const os = osName.toLowerCase();
    if (
        os.includes("mac") ||
        os.includes("darwin") ||
        os === "ios" ||
        os.includes("osx")
    ) {
        return "/mac.png";
    }

    // 🪟 Windows
    if (os.includes("windows")) {
        return "/windows.png";
    }

    // 🐧 Linux family
    if (os.includes("ubuntu")) {
        return "/ubuntu.png";
    }

    if (os.includes("kali")) {
        return "/kali-linux.svg";
    }

    if (os.includes("centos")) {
        return "/centos.png";
    }

    if (os.includes("almalinux")) {
        return "/AlmaLinux.svg";
    }

    if (os.includes("suse")) {
        return "/suse.svg";
    }

    if (os.includes("debian")) {
        return "/debian.svg";
    }

    if (os.includes("red hat")) {
        return "/redhat.svg";
    }

    if (os.includes("fedora")) {
        return "/Fedora.svg";
    }

    if (
        os.includes("linux")
    ) {
        return "/linux.svg";
    }

    // 🤖 Android
    if (os.includes("android")) {
        return "/android.svg";
    }

    // 📦 Default
    return "/device.svg";
};

const renderOSIcon = (osName?: string, className: string = "w-4 h-4 object-contain shrink-0") => {
    const src = getOSIconPath(osName);
    return (
        <img
            src={src}
            alt={osName || "OS"}
            className={className}
            onError={(e) => {
                (e.target as HTMLImageElement).src = "/linux.svg";
            }}
        />
    );
};

// Custom API Search dropdown for Server Assets
const ServerSearchDropdown: React.FC<{
    level: any;
    currentAssetId: number | string;
    currentName: string;
    currentOSName?: string;
    path: string;
    onSelectServer: (server: any) => void;
}> = ({ level, currentAssetId, currentName, currentOSName, path, onSelectServer }) => {
    const [isOpen, setIsOpen] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [servers, setServers] = useState<any[]>([]);
    const [loading, setLoading] = useState(false);
    const containerRef = useRef<HTMLDivElement>(null);

    const info = JSON.parse(sessionStorage.getItem("internal_ids") || "{}");

    useEffect(() => {
        if (!isOpen) return;

        const timer = setTimeout(async () => {
            setLoading(true);
            try {
                const queryObj: any = level?.id
                    ? { client_id: level.id }
                    : {};

                queryObj.asset_type = "local_asset"; // Server assets API filter as in AssetsTable.tsx

                if (searchTerm.trim()) {
                    queryObj.$or = [
                        { name: { "$ilike": `%${searchTerm.trim()}%` } },
                        { hostname: { "$ilike": `%${searchTerm.trim()}%` } },
                        { ip_address: { "$ilike": `%${searchTerm.trim()}%` } },
                        { os_name: { "$ilike": `%${searchTerm.trim()}%` } }
                    ];
                }

                const params = {
                    query: JSON.stringify(queryObj),
                    limit: 50,
                    sort_by: 'name',
                    sort_order: 'ASC'
                };

                const response = await baseapi.get('/assets', { params });
                if (response.data?.status && Array.isArray(response.data.data)) {
                    setServers(response.data.data);
                } else {
                    setServers([]);
                }
            } catch (error) {
                console.error("Failed to fetch server assets:", error);
                setServers([]);
            } finally {
                setLoading(false);
            }
        }, 300);

        return () => clearTimeout(timer);
    }, [isOpen, searchTerm, level, info.client_id]);

    useEffect(() => {
        const handleClickOutside = (e: MouseEvent) => {
            if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
                setIsOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const handleSelectServer = (server: any) => {
        const updatedInfo = {
            ...info,
            is_ad_machine: server.is_ad_machine || false,
            asset_id: server.id,
            client_id: server.client_id,
            agent_id: server.agent_id,
            organization_id: server.organization_id,
            asset_type: server.asset_type || "local_asset",
            host_name: server.hostname || server.name,
            name: server.name,
            os_name: server.os_name || "",
            has_oob: server.has_oob || false,
            oob_type: server.oob_type || "",
            path: path
        };
        sessionStorage.setItem('internal_ids', JSON.stringify(updatedInfo));
        onSelectServer(updatedInfo);
        setIsOpen(false);
    };

    return (
        <div className="relative" ref={containerRef}>
            <button
                type="button"
                onClick={() => setIsOpen(!isOpen)}
                className="flex items-center justify-between gap-2 px-3 py-2 bg-white dark:bg-gray-800 hover:bg-slate-50 dark:hover:bg-gray-700 border border-slate-200 dark:border-gray-700 rounded-md text-slate-800 dark:text-white text-xs font-semibold cursor-pointer transition-colors shadow-sm w-72"
                title="Select / Search Server"
            >
                <div className="flex items-center gap-2 truncate min-w-0">
                    {renderOSIcon(info.os_name || currentOSName, "w-4 h-4 object-contain shrink-0")}
                    <span className="truncate">{currentName || "Select Server..."}</span>
                </div>
                <ChevronDown className={`w-3.5 h-3.5 text-slate-400 dark:text-gray-400 shrink-0 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
            </button>

            {isOpen && (
                <div className="absolute right-0 mt-1.5 w-72 bg-white dark:bg-gray-800 border border-slate-200 dark:border-gray-700 rounded-lg shadow-xl z-[9999] overflow-hidden">
                    <div className="p-2 border-b border-slate-200 dark:border-gray-700 bg-slate-50 dark:bg-gray-800/90">
                        <div className="relative">
                            <Search className="w-3.5 h-3.5 text-slate-400 dark:text-gray-400 absolute left-2.5 top-2.5" />
                            <input
                                type="text"
                                autoFocus
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                placeholder="Search server..."
                                className="w-full bg-white dark:bg-gray-900 border border-slate-200 dark:border-gray-700 rounded-md pl-8 pr-7 py-1.5 text-xs text-slate-900 dark:text-white placeholder-slate-400 dark:placeholder-gray-500 focus:outline-none focus:border-orange-500 transition-colors"
                            />
                            {searchTerm && (
                                <button
                                    onClick={() => setSearchTerm('')}
                                    className="absolute right-2 top-2 text-slate-400 hover:text-slate-700 dark:text-gray-400 dark:hover:text-white"
                                >
                                    <X className="w-3.5 h-3.5" />
                                </button>
                            )}
                        </div>
                    </div>

                    <div className="max-h-60 overflow-y-auto divide-y divide-slate-100 dark:divide-gray-700/50">
                        {loading ? (
                            <div className="flex items-center justify-center p-4 text-slate-500 dark:text-gray-400 text-xs gap-2">
                                <Loader2 className="w-4 h-4 animate-spin text-emerald-500 dark:text-emerald-400" />
                                <span>Loading servers...</span>
                            </div>
                        ) : servers.length === 0 ? (
                            <div className="p-4 text-center text-slate-500 dark:text-gray-400 text-xs">
                                No servers found
                            </div>
                        ) : (
                            servers.map((server) => {
                                const isSelected = Number(server.id) === Number(currentAssetId);
                                return (
                                    <button
                                        key={server.id}
                                        onClick={() => handleSelectServer(server)}
                                        className={`w-full text-left px-3 py-2 flex items-center justify-between hover:bg-slate-50 dark:hover:bg-gray-700/60 transition-colors ${isSelected ? 'bg-orange-500/10' : ''}`}
                                    >
                                        <div className="flex items-center gap-2.5 min-w-0 pr-2">
                                            {renderOSIcon(server.os_name, "w-4 h-4 object-contain shrink-0")}
                                            <div className="min-w-0">
                                                <div className="text-xs font-semibold text-slate-900 dark:text-white truncate">
                                                    {server.name || server.hostname || `Server #${server.id}`}
                                                </div>
                                                <div className="text-[10px] text-slate-500 dark:text-gray-400 truncate flex items-center gap-1.5 mt-0.5">
                                                    <span>{server.ip_address || 'No IP'}</span>
                                                    {server.os_name && (
                                                        <>
                                                            <span>•</span>
                                                            <span className="truncate max-w-[110px]">{server.os_name}</span>
                                                        </>
                                                    )}
                                                </div>
                                            </div>
                                        </div>
                                        {isSelected && (
                                            <Check className="w-4 h-4 text-emerald-600 dark:text-emerald-400 shrink-0" />
                                        )}
                                    </button>
                                );
                            })
                        )}
                    </div>
                </div>
            )}
        </div>
    );
};

// Validate if a tab is supported by the active asset / OS platform
const isTabValid = (tabId: string, currentAssetInfo: any, currentInfo: any): boolean => {
    if (!tabId || tabId === "overview") return true;

    const assetType = currentInfo?.asset_type || "";
    const agentType = currentInfo?.agent_type || "";

    // For switch, camera, probe, ilo, and other specialized devices, isTabValid should not apply
    if (
        assetType === "switch" ||
        assetType === "camera" ||
        assetType === "ilo" ||
        agentType === "probe" ||
        (currentInfo?.has_oob && currentInfo?.oob_type?.toLowerCase() === "ilo" && assetType !== "local_asset")
    ) {
        return true;
    }

    const platform = (currentAssetInfo?.platform || currentInfo?.os_name || "").toLowerCase();
    const isLinux = platform.includes("linux") || platform.includes("ubuntu") || platform.includes("debian") || platform.includes("centos") || platform.includes("redhat") || platform.includes("suse") || platform.includes("fedora");
    const isDarwin = platform.includes("darwin") || platform.includes("macos") || platform.includes("mac");

    // 1. Windows only tabs - invalid on Linux / Darwin
    const windowsOnlyTabs = ["license", "patches", "windows-defender", "shared-folders", "scheduled-tasks", "security"];
    if ((isLinux || isDarwin) && windowsOnlyTabs.includes(tabId)) {
        return false;
    }

    // 2. Hide RAID on Darwin
    if (isDarwin && tabId === "raid") {
        return false;
    }

    // 3. Linux only tabs - invalid on non-Linux
    const linuxOnlyTabs = ["cron-jobs", "kernel-modules"];
    if (!isLinux && linuxOnlyTabs.includes(tabId)) {
        return false;
    }

    return true;
};

const AssetDashboardLayout: React.FC<{ children?: React.ReactNode }> = () => {
    const [info, setInfo] = useState<any>(() => {
        return JSON.parse(sessionStorage.getItem("internal_ids") || "{}");
    });
    const [assetInfo, setAssetInfo] = useState<any>(null);
    const [snmpData, setSnmpData] = useState<any>(null);
    const [isLoading, setIsLoading] = useState<boolean>(true);
    const [hasData, setHasData] = useState<boolean>(true);
    const level = useClientLevel();

    const navigate = useNavigate();
    const location = useLocation();
    const isFirstRender = useRef(true);

    // 1. Get info first to determine agent_type
    const organizationId = info.organization_id;
    const agent_id = info.agent_id;
    const asset_id = info.asset_id;
    const client_id = info.client_id;
    const agent_type = info.agent_type;
    const asset_type = info.asset_type;
    const has_oob = info.has_oob;
    const oob_type = info.oob_type;
    const path = info.path;
    const is_ad_machine = info.is_ad_machine;
    const [scanLoading, setScanLoading] = useState<boolean>(false);

    const handleServerChange = (updatedInfo: any) => {
        setInfo(updatedInfo);
        // If current tab is not supported by the new asset, reset to default tab
        if (!isTabValid(activeTab, null, updatedInfo)) {
            const nextDefaultTab = updatedInfo.asset_type === "camera" ? "camera-overview" : (updatedInfo.asset_type === "switch" ? "discovered-interfaces" : (updatedInfo.asset_type === "ilo" ? "assetsilo" : (updatedInfo.agent_type === "probe" ? "vulnerabilities" : "overview")));
            const params = new URLSearchParams(location.search);
            params.set("tab", nextDefaultTab);
            params.delete("serviceId");
            navigate({ search: params.toString() }, { replace: true });
        }
    };

    const handleTriggerScan = async () => {
        if (!agent_id) {
            showError("Agent ID is missing for this asset.");
            return;
        }
        try {
            setScanLoading(true);
            const payload = {
                client_id: client_id || level?.id,
                agent_id: agent_id,
                job_type: 'local_asset_scan',
                parameters: {}
            };
            const res = await baseapi.post('/agents/agent-task', payload);
            if (res.data?.status) {
                showSuccess(res.data.message || 'Scan task initiated successfully');
            } else {
                showError(res.data?.message || 'Failed to initiate scan task');
            }
        } catch (err: any) {
            showError(err.message || 'Error initiating scan task');
        } finally {
            setScanLoading(false);
        }
    };

    // 2. Determine default tab based on agent_type or asset_type
    const defaultTab = asset_type === "camera" ? "camera-overview" : (asset_type === "switch" ? "discovered-interfaces" : (asset_type === "ilo" ? "assetsilo" : (agent_type === "probe" ? "vulnerabilities" : (asset_id === 3933 ? "aseetsilo" : "overview"))));

    // 3. Pass defaultTab to helper
    const activeTab = getTabFromParams(location.search, defaultTab);

    const fetchSystemInfo = useCallback(async () => {
        if (asset_type === "camera" || asset_type === "switch" || asset_type?.toLowerCase() === "unknown" || asset_type?.toLowerCase() === "firewall" || asset_type?.toLowerCase() === "ilo") {
            setHasData(true);
            setIsLoading(false);
            return;
        }

        setIsLoading(true);
        const params = {
            query: JSON.stringify({
                client_id,
                organization_id: organizationId,
                agent_id,
                asset_id,
            }),
        };

        try {
            // Parallel requests array
            const requests = [baseapi.get("/system_info", { params })];

            // If probe, add snmp request
            if (agent_type === "probe") {
                requests.push(baseapi.get("/snmpinfo", { params }));
            }

            const responses = await Promise.allSettled(requests);

            // Handle System Info (First request)
            const sysResponse = responses[0];

            if (sysResponse.status === "fulfilled" && sysResponse.value.data.status && sysResponse.value.data.data?.length > 0) {
                setAssetInfo(sysResponse.value.data.data[0]);
            } else {
                setAssetInfo(null);
            }

            // Handle SNMP Info (Second request if probe)
            if (agent_type === "probe" && responses[1]) {
                const snmpResponse = responses[1];
                if (snmpResponse.status === "fulfilled" && snmpResponse.value.data.status && snmpResponse.value.data.data?.length > 0) {
                    setSnmpData(snmpResponse.value.data.data[0]);
                } else {
                    setSnmpData(null);
                }
            } else {
                setSnmpData(null);
            }

            setHasData(true);

        } catch (error: any) {
            setHasData(false);
            showError(error.message);
        } finally {
            setIsLoading(false);
        }
    }, [asset_type, agent_type, client_id, organizationId, agent_id, asset_id]);

    function PlatformIcon({ osName }: { osName: string }) {

        const os = osName?.toLowerCase() || "";

        if (
            os.includes("mac") ||
            os.includes("darwin") ||
            os === "ios" ||
            os.includes("osx")
        ) {
            return <img src="/mac.png" alt="Mac" className="h-8 w-8" />;
        }

        // 🪟 Windows
        if (os.includes("windows")) {
            return <img src="/windows.png" alt="Windows" className="h-8 w-8" />;
        }

        // 🐧 Linux family
        if (os.includes("ubuntu")) {
            return <img src="/ubuntu.png" alt="Ubuntu" className="h-8 w-8" />;
        }

        if (os.includes("kali")) {
            return <img src="/kali-linux.svg" alt="Kali Linux" className="h-8 w-8" />;
        }

        if (os.includes("centos")) {
            return <img src="/centos.png" alt="CentOS" className="h-8 w-8" />;
        }

        if (os.includes("almalinux")) {
            return <img src="/AlmaLinux.svg" alt="AlmaLinux" className="h-8 w-8" />;
        }

        if (os.includes("suse")) {
            return <img src="/suse.svg" alt="Suse" className="h-8 w-8" />;
        }

        if (os.includes("debian")) {
            return <img src="/debian.svg" alt="Debian" className="h-8 w-8" />;
        }

        if (os.includes("red hat")) {
            return <img src="/redhat.svg" alt="RedHat" className="h-8 w-8" />;
        }

        if (os.includes("fedora")) {
            return <img src="/Fedora.svg" alt="Fedora" className="h-8 w-8" />;
        }

        if (
            os.includes("linux")
        ) {
            return <img src="/linux.svg" alt="Linux" className="h-8 w-8" />;
        }

        // 🤖 Android
        if (os.includes("android")) {
            return <img src="/android.svg" alt="Android" className="h-8 w-8" />;
        }

        // 📦 Default
        return <img src="/device.svg" alt="Device" className="h-8 w-8" />;
    }

    useEffect(() => {
        fetchSystemInfo();
    }, [fetchSystemInfo]);

    // Validate active tab against loaded system/platform info and fallback to defaultTab if invalid
    useEffect(() => {
        if (!isLoading) {
            if (!isTabValid(activeTab, assetInfo, info)) {
                const params = new URLSearchParams(location.search);
                params.set("tab", defaultTab);
                params.delete("serviceId");
                navigate({ search: params.toString() }, { replace: true });
            }
        }
    }, [activeTab, assetInfo, info, defaultTab, isLoading, location.search, navigate]);

    useEffect(() => {
        if (isFirstRender.current) {
            isFirstRender.current = false;
            return; // ⛔ skip initial load
        }

        navigate("/asset");
    }, [level, navigate]);

    if (isLoading) {
        return (
            <div className="flex h-screen bg-slate-50 dark:bg-gray-900 items-center justify-center">
                <div className="text-slate-600 dark:text-gray-300 animate-pulse text-lg">
                    Loading system info...
                </div>
            </div>
        );
    }

    if (!hasData) {
        return (
            <div className="flex flex-col h-screen bg-slate-50 dark:bg-gray-900 items-center justify-center text-slate-600 dark:text-gray-300">
                <p className="text-lg mb-4 font-medium">No system information found.</p>
                <button
                    onClick={() => navigate(-1)}
                    className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-md transition-colors"
                >
                    ← Go Back
                </button>
            </div>
        );
    }

    // Common data
    const rawUpdated = assetInfo?.updated ?? "";

    const utcString = rawUpdated
        ? (rawUpdated.endsWith("Z") ? rawUpdated : `${rawUpdated}Z`)
        : "";

    const lastUpdated = utcString
        ? new Date(utcString).toLocaleString(undefined, {
            year: "numeric",
            month: "short",
            day: "numeric",
            hour: "2-digit",
            minute: "2-digit",
            second: "2-digit",
            hour12: true,
        })
        : "";

    const platform = assetInfo?.platform ?? "";

    // Specific data based on agent type
    const isProbe = agent_type === "probe";

    // Standard Header Data
    const hostName = assetInfo?.host_name ?? "";
    const domainInfo = assetInfo?.domain_info ?? "";
    const osName = assetInfo?.os_name ?? "";

    // Probe Header Data (Fallback to assetInfo if snmpData missing, or vice versa)
    const macAddress = assetInfo?.mac || "-";
    const manufacturer = assetInfo?.manufacturer || "-";

    // if(asset_type === 'ilo')
    //     return <IloMain assetId={asset_id} />

    if (asset_type === "camera") {
        return <CompactCameraDashboard />;
    }

    if (asset_type === "switch") {
        return <CompactSwitchDashboard />;
    }

    if (asset_type === "firewall") {
        return <FirewallMain />;
    }

    if (asset_type === "nvr") {
        return <NvrPage />;
    }

    if (asset_type === "access_point") {
        return <APmain />;
    }

    if ((asset_type?.toLowerCase() === "unknown" || (!assetInfo && !snmpData)) && asset_type?.toLowerCase() !== "ilo") {
        return (
            <div className="h-screen flex flex-col bg-slate-50 dark:bg-slate-950 overflow-hidden">
                <div className="p-4 border-b border-slate-200 dark:border-gray-500 flex items-center justify-between bg-white dark:bg-slate-900 backdrop-blur-xl sticky top-0 z-50 shadow-sm dark:shadow-none">
                    <div className="flex items-center gap-4">
                        <button
                            onClick={() => navigate(path)}
                            className="p-2 hover:bg-slate-100 dark:hover:bg-white/5 rounded-xl text-slate-500 hover:text-slate-900 dark:text-slate-400 dark:hover:text-white transition-all group active:scale-95"
                        >
                            <ArrowLeft className="w-5 h-5 group-hover:-translate-x-1 transition-transform" />
                        </button>
                        <div className="flex flex-col">
                            <h2 className="text-lg font-black text-slate-900 dark:text-white uppercase tracking-tight flex items-center gap-2">
                                <Fingerprint className="w-5 h-5 text-orange-500" />
                                {!asset_type || asset_type.toLowerCase() === "unknown"
                                    ? "Unknown Asset"
                                    : asset_type
                                        .toLowerCase()
                                        .replace(/_/g, " ")
                                        .replace(/\b\w/g, (c: any) => c.toUpperCase())
                                }
                            </h2>
                            <p className="text-[10px] font-bold text-slate-500 uppercase tracking-widest">{info.name || info.ip_address}</p>
                        </div>
                    </div>
                </div>

                <div className="flex-1 overflow-hidden bg-slate-100 dark:bg-gray-900">
                    <ServerProtocols />
                </div>
            </div>
        );
    }

    return (
        <div className="flex h-screen bg-slate-100 dark:bg-gray-900">
            <AssetSidebarTabs
                key={asset_id}
                activeTab={activeTab}
                onTabChange={(tab: string) => {
                    const params = new URLSearchParams(location.search);
                    params.set("tab", tab);
                    params.delete("serviceId");
                    navigate({ search: params.toString() }, { replace: true });
                }}
                info={{
                    ...assetInfo,
                    agent_type: agent_type,
                    asset_type: asset_type,
                    hasSnmpData: !!snmpData,
                    has_oob: has_oob,
                    oob_type: oob_type
                }}
            />
            <div className="flex flex-col flex-1 min-w-0 min-h-0 overflow-hidden">
                {asset_type !== "camera" && (
                    <div className="bg-gray-50 dark:bg-gray-900 border-b border-slate-300 dark:border-gray-700 flex items-center px-4 py-2 space-x-4 shadow-sm dark:shadow-none">
                        <div className="flex items-center space-x-3">
                            {asset_type !== "ilo" && <PlatformIcon osName={osName} />}
                        </div>

                        {isProbe ? (
                            <>
                                {/* Probe Specific Header */}
                                <div className="space-y-0.5">
                                    <span className="text-[10px] text-slate-500 dark:text-gray-400 block leading-tight">MAC Address</span>
                                    <span className="text-sm text-slate-900 dark:text-white font-semibold leading-tight break-all">
                                        {macAddress}
                                    </span>
                                </div>
                                <div className="space-y-0.5">
                                    <span className="text-[10px] text-slate-500 dark:text-gray-400 block leading-tight">Manufacturer</span>
                                    <span className="text-xs text-slate-700 dark:text-gray-200 leading-tight truncate max-w-[180px]" title={manufacturer}>
                                        {manufacturer}
                                    </span>
                                </div>
                            </>
                        ) : asset_type === "ilo" ? (
                            null
                        ) : (
                            <>
                                {/* Standard Header */}
                                <div className="space-y-0.5">
                                    <span className="text-[10px] text-slate-500 dark:text-gray-400 block leading-tight">Host Name</span>
                                    <span className="text-sm text-slate-900 dark:text-white font-semibold leading-tight truncate max-w-[220px]" title={hostName}>
                                        {hostName}
                                    </span>
                                </div>
                                <div className="space-y-0.5">
                                    <span className="text-[10px] text-slate-500 dark:text-gray-400 block leading-tight">Domain</span>
                                    <span className="text-xs text-slate-700 dark:text-gray-200 leading-tight truncate max-w-[200px]" title={domainInfo}>
                                        {domainInfo || "-"}
                                    </span>
                                </div>
                            </>
                        )}

                        {asset_type !== "ilo" && (
                            <div className="space-y-0.5">
                                <span className="text-[10px] text-slate-500 dark:text-gray-400 block leading-tight">Last Updated</span>
                                <span className="text-xs text-slate-700 dark:text-gray-200 leading-tight truncate max-w-[200px]" title={lastUpdated}>
                                    {lastUpdated}
                                </span>
                            </div>
                        )}
                        <div className="ml-auto flex items-center gap-2">
                            <ServerSearchDropdown
                                level={level}
                                currentAssetId={asset_id}
                                currentName={hostName || info.name || info.host_name}
                                currentOSName={osName || info.os_name}
                                path={path}
                                onSelectServer={handleServerChange}
                            />
                            <button
                                type="button"
                                onClick={handleTriggerScan}
                                disabled={scanLoading}
                                className="flex items-center gap-1.5 px-3 py-1 bg-emerald-600 hover:bg-emerald-500 dark:bg-emerald-700 dark:hover:bg-emerald-600 disabled:opacity-50 text-white rounded-md text-xs font-semibold cursor-pointer transition-colors shadow-sm h-8 shrink-0"
                                title="Trigger Local Asset Scan"
                            >
                                {scanLoading ? (
                                    <Loader2 className="w-3.5 h-3.5 animate-spin text-white" />
                                ) : (
                                    <ScanSearch className="w-3.5 h-3.5 text-white" />
                                )}
                                <span>{scanLoading ? 'Scanning...' : 'Scan'}</span>
                            </button>
                            {is_ad_machine && (
                                <button className="flex items-center gap-1 px-2 py-2 bg-slate-100 hover:bg-slate-200 dark:bg-gray-700/20 dark:hover:bg-blue-600/10 text-slate-800 dark:text-white border border-slate-200 dark:border-transparent rounded-md cursor-pointer w-32 h-8 transition-colors"
                                    onClick={() => navigate('/asset/active-directory-v2')}>
                                    <img src="/active-directory.svg" alt="Active Directory" className="w-30 h-4 text-gray-200" />
                                </button>
                            )}
                            <button
                                onClick={() => navigate(path)}
                                className="px-4 py-2 bg-orange-600 hover:bg-orange-500 dark:bg-orange-700 dark:hover:bg-orange-600 flex items-center gap-1 text-white rounded-md transition-colors shadow-sm"
                            >
                                <ArrowLeft className="w-4 h-4" />
                                <span className="text-xs font-semibold">Back</span>
                            </button>
                        </div>
                    </div>
                )}
                <main key={asset_id} className="flex-1 p-1 pb-20 overflow-y-auto bg-slate-100 dark:bg-gray-900">
                    {/* Standard Tabs */}
                    {activeTab === "overview" ? (
                        <Overview assetInfo={assetInfo} handleTabChange={(tab: string) => {
                            const params = new URLSearchParams(location.search);
                            params.set("tab", tab);
                            navigate({ search: params.toString() }, { replace: true });
                        }} />
                    ) : activeTab === "license" ? (
                        <LicenseInfo {...assetInfo} />
                    ) : activeTab === "raid" ? (
                        <RaidInfo {...assetInfo} />
                    ) : activeTab === "hardware" ? (
                        <HardwareInfo {...assetInfo} />
                    ) : activeTab === "memory" ? (
                        <MemoryInfo {...assetInfo} />
                    ) : activeTab === "performance" ? (
                        <PerformanceMetrics {...assetInfo} />
                    ) : activeTab === "software" ? (
                        <SoftwareInfo {...assetInfo} />
                    ) : activeTab === "users" ? (
                        <UserList {...assetInfo} />
                    ) : activeTab === "services" ? (
                        <ServiceList {...assetInfo} />
                    ) : activeTab === "patches" ? (
                        <PatchList {...assetInfo} />
                    ) : activeTab === "security" ? (
                        <AssetSecurity asset_id={asset_id} client_id={client_id} agent_id={agent_id} organization_id={organizationId} />
                    ) : activeTab === "certificates" ? (
                        <SSL {...assetInfo} />
                    ) : activeTab === "interfaces" ? (
                        <NetworkInterfaces {...assetInfo} />
                    ) : activeTab === "issues" ? (
                        <SystemIssues {...assetInfo} />
                    ) : activeTab === "network-firewall" ? (
                        <NetworkFirewall {...assetInfo} />
                    ) : activeTab === "pci-devices" ? (
                        <PCIDevices {...assetInfo} />
                    ) : activeTab === "processes" ? (
                        <ProcessList {...assetInfo} />
                    ) : activeTab === "vulnerabilities" ? (
                        <AssetVulnerabilities {...assetInfo} />
                    ) : activeTab === "ports" ? (
                        <OpenPorts {...assetInfo} />
                    ) : activeTab === "compliance" ? (
                        <NewAssetComplianceDashboard {...assetInfo} />
                    ) : activeTab === "discovered-interfaces" && asset_type === "switch" ? (
                        <DiscoveredInterfaces />
                    ) : activeTab === "switch-ssh" && asset_type === "switch" ? (
                        <SwitchSSH asset_id={asset_id} client_id={client_id} />
                    ) : activeTab === "discovered-neighbors" && asset_type === "switch" ? (
                        <DiscoveredNeighbors />
                    ) : activeTab === "switch-hardware" && asset_type === "switch" ? (
                        <SwitchHardwareInfo />
                    ) : activeTab === "vlans" && asset_type === "switch" ? (
                        <VlansInfo />
                    ) : activeTab === "arp-entries" && asset_type === "switch" ? (
                        <ArpEntries />
                    ) : activeTab === "mac-table" && asset_type === "switch" ? (
                        <MacTableInfo />
                    ) : activeTab === "tcp-connections" && asset_type === "switch" ? (
                        <TcpConnections />
                    ) : activeTab === "udp-listeners" && asset_type === "switch" ? (
                        <UdpListeners />
                    ) : activeTab === "poe" && asset_type === "switch" ? (
                        <PoeInfo client_id={client_id} />
                    ) : activeTab === "switch-ports" && asset_type === "switch" ? (
                        <SwitchPorts />
                    ) : activeTab === "camera-interface" && asset_type === "camera" ? (
                        <CameraInterface />
                    ) : activeTab === "protocol" && asset_type === "camera" ? (
                        <CameraProtocols />
                    ) : activeTab === "camera-overview" && asset_type === "camera" ? (
                        <CameraOverview />
                    ) : activeTab === "camera-users" && asset_type === "camera" ? (
                        <CameraUsers />
                    ) : activeTab === "protocol-discovery" ? (
                        <ServerProtocols />
                    ) : activeTab === "service_monitoring" ? (
                        <AssetServices assetId={asset_id} />
                    ) : activeTab === "assetsilo" ? (
                        <AssetIlo assetId={asset_id} />
                    ) : activeTab === "ilo-event-logs" ? (
                        <AssetIloEventLogs assetId={asset_id} />
                    ) : activeTab === "ilo-issues" ? (
                        <AssetIloIssues assetId={asset_id} />
                    ) : activeTab === "ilo-temperatures" ? (
                        <AssetIloTemperatures assetId={asset_id} />
                    ) : activeTab === "ilo-firmware" ? (
                        <AssetIloFirmware assetId={asset_id} />
                    ) : activeTab === "ilo-protocols" ? (
                        <AssetIloProtocols assetId={asset_id} />
                    ) : activeTab === "windows-defender" ? (
                        <AssetWindowsDefender assetId={asset_id} client_id={client_id} />
                    ) : activeTab === "shared-folders" ? (
                        <AssetSharedFolders assetId={asset_id} client_id={client_id} />
                    ) : activeTab === "scheduled-tasks" ? (
                        <AssetScheduledTasks assetId={asset_id} client_id={client_id} />
                    ) : activeTab === "firewall-rules" ? (
                        <AssetFirewallRules assetId={asset_id} client_id={client_id} />
                    ) : activeTab === "logon-history" ? (
                        <AssetLogonHistory assetId={asset_id} client_id={client_id} platform={platform} />
                    ) : activeTab === "cron-jobs" ? (
                        <CronJobs asset_id={asset_id} client_id={client_id} />
                    ) : activeTab === "kernel-modules" ? (
                        <KernelModules asset_id={asset_id} client_id={client_id} />
                    ) : activeTab === "containers" ? (
                        <AssetContainers asset_id={asset_id} client_id={client_id} />
                    ) : activeTab === "log-monitor" ? (
                        <LogMonitor asset_id={asset_id} client_id={client_id} agent_id={agent_id} organization_id={organizationId} />
                    )

                        // SNMP Tabs (Placeholder components)
                        : activeTab === "snmp-info" ? (
                            <div className="text-slate-900 dark:text-white"><SnmpInfo data={snmpData} /></div>
                        ) : activeTab === "snmp-hardware" ? (
                            <div className="text-slate-900 dark:text-white"><SnmpHardware {...assetInfo} /></div>
                        ) : activeTab === "snmp-interfaces" ? (
                            <div className="text-slate-900 dark:text-white"><SnmpInterface {...assetInfo} /></div>
                        ) : activeTab === "ports_probe" ? (
                            <div className="text-slate-900 dark:text-white"><ProbePorts {...assetInfo} /> </div>
                        ) : activeTab === "db-monitor" ? (
                            <AssetDBMonitor agent_id={agent_id} organization_id={organizationId} asset_id={asset_id} client_id={client_id} />
                        )

                            : (
                                <div className="text-xl font-semibold text-slate-900 dark:text-white">
                                    Active Tab: <span className="font-mono">{activeTab}</span>
                                </div>
                            )}
                </main>
            </div>
        </div>
    );
};

export default AssetDashboardLayout;
